import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import { Typography } from "@mui/material";
import {
    DataGrid,
    GridFilterModel, GridSortModel, GridLinkOperator, GridRowId, GridColDef, GridRenderCellParams
} from "@mui/x-data-grid";
import { useAuth } from "../../_auth/useAuth";
import {
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, ListViewToolbar,
    DialogBox, IDialogTextProps,
    MessageBox, MsgBoxType, IMessagePanelProps,
    MuiGridStateProp, MuiGridDataProp, muiGridFilterIsValid, FormTitle
} from "../_shared";
import { IItemIdsRequest, IMuiDataRequest, customerApi } from "../../_api";
import { AuthRolesEnum, companyViewColumns } from "../../_appData";
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';

export const CompanyListView: React.FC = () => {
    const { isAuthenticated, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    
    // Create columns with actions column that uses navigate with state
    const columns = useMemo(() => {
        const actionsColumn: GridColDef = {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/company/${params.row.company_id}`, {
                                state: { mode: 'view' }
                            });
                        }}
                        sx={{ 
                            padding: '4px',
                            color: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                );
            },
        };
        return [actionsColumn, ...companyViewColumns];
    }, [navigate]);
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const getGridDataRef = useRef<() => Promise<void>>();
    const [dlgBoxOpen, setDlgBoxOpen] = React.useState<boolean>(false);
    const [dlgBoxProps, setDlgBoxProps] = useState<IDialogTextProps>({ message: "" });
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridState, setGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [{ field: 'modify_datetime', sort: 'desc' }],
    });
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsLoading(true);

        const options: IItemIdsRequest = { item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        // Note: customerApi doesn't have a batch remove method, so we handle it individually
        // You may need to implement batch delete in the backend API
        const deletePromises = row_ids.map(id => 
            customerApi.remove({ company_id: id } as any, auth_token)
        );

        Promise.all(deletePromises)
            .then((responses) => {
                const successCount = responses.filter(resp => resp?.success).length;
                if (successCount > 0) {
                    // Call getGridData via ref to refresh the grid
                    if (getGridDataRef.current) {
                        getGridDataRef.current();
                    }
                    setSelectionModel([]);
                    msgBox_Show(
                        `${successCount} Company items have been deleted...`,
                        undefined,
                        "info"
                    );
                } else {
                    msgBox_Show(
                        "An error occurred while deleting Company data... " +
                        "please retry your last action. If you continue to experience issues, " +
                        "contact customer support.",
                        "Unable to Delete Company data...",
                        "warning"
                    );
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [msgBox_Show, getAccessToken]);

    const dlgBox_Close = useCallback((value?: string) => {
        const selRows = selectionModel;
        setDlgBoxOpen(false);

        if (value && value === "ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }, [selectionModel, deleteSelectedItems]);

    const dlgBox_Show = (message: string, title?: string) => {
        setDlgBoxProps({
            message: message,
            title: title,
        })
        setDlgBoxOpen(true);
    }

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const onGridPageChange = (page: number) => {
        setGridState((prev) => ({
            ...prev,
            page: page
        }));
    };

    const onGridPageSizeChange = (size: number) => {
        setGridState((prev) => ({
            ...prev,
            page_size: size
        }));
    };

    const onGridFilterChange = (model: GridFilterModel) => {
        if (muiGridFilterIsValid(model)) {
            setGridState((prev) => ({
                ...prev,
                filters: model
            }));
        }
    };

    const onGridSortChange = (model: GridSortModel) => {
        setGridState((prev) => ({
            ...prev,
            sorts: model
        }));
    };

    const onClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        navigate("/company/new");
    };

    const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if (selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be edited",
                "Edit Company...",
                "warning"
            );
        if (selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be edited",
                "Edit Company...",
                "warning"
            );

        if (selRows.length === 1) {
            navigate(`/company/${selRows[0]}`);
        }
    };

    const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgBox_close for processing of dialog result
        dlgBox_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const execSearch = (term: string) => {
        // Modify Grid Filter model
        let model: GridFilterModel = {
            items: [],
            linkOperator: GridLinkOperator.Or
        }
        // Search company_sid, CompanyName
        if (term.length > 0) {
            model.items.push({ id: 1001, columnField: "company_sid", operatorValue: "contains", value: term });
            model.items.push({ id: 1002, columnField: "company_name", operatorValue: "contains", value: term });
        }

        setGridState((prev) => ({
            ...prev,
            filters: model
        }));
    };

    const onClickReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        setGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    };

    const getGridData = useCallback(async () => {
        setIsLoading(true);

        const options: IMuiDataRequest = gridState;
        const auth_token: string = await getAccessToken();

        customerApi.get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        setGridData({
                            rows: resp.data,
                            row_count: resp.count!
                        });
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving Company data... \n" +
                            "please retry your last action. If you continue to experience issues, " +
                            "please contact customer support.",
                            "Unable to Load Company data...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [gridState, getAccessToken, msgBox_Show]);

    // Store getGridData in ref so it can be called from deleteSelectedItems
    useEffect(() => {
        getGridDataRef.current = getGridData;
    }, [getGridData]);

    // Track previous values to prevent unnecessary API calls
    const prevValuesRef = useRef<string>('');

    useEffect(() => {
        // Create a stable key from the values that matter
        const currentKey = JSON.stringify({
            page: gridState.page,
            page_size: gridState.page_size,
            filters: gridState.filters,
            sorts: gridState.sorts
        });

        // Only call API if values actually changed
        if (prevValuesRef.current !== currentKey) {
            prevValuesRef.current = currentKey;
            getGridData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridState.page, gridState.page_size, gridState.filters, gridState.sorts]);

    const lvGridToolbar = () => {
        const isAdmin = userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin,
        AuthRolesEnum.ListOwner, AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]);
        const isROUser = userHasPrivilege([AuthRolesEnum.PafUser]);
        return (
            <ListViewToolbar
                onClickNew={onClickNew}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickReset={onClickReset}
                execSearch={execSearch}
                canView={isAdmin || isROUser}
                canAdd={isAdmin}
                canUpdate={isAdmin}
                canDelete={isAdmin}
            />
        );
    }

    return (
        <>
            <Box
                sx={{
                    paddingTop: 3,
                }}
            >
                <FormTitle
                    title={"Companies"}
                    titleIcon={"business_rounded_icon"}
                    typeVariant={"h4"}
                />
                {isAuthenticated ? (
                    <Box sx={{ marginTop: 2 }}>
                        <DaGridContainer
                            sx={{
                                height: 600,
                                width: '100%',
                            }}
                        >
                            <DataGrid
                                components={{
                                    LoadingOverlay: DaGridLoadingOverlay,
                                    Toolbar: lvGridToolbar,
                                }}
                                loading={isLoading}
                                columns={columns}
                                rows={gridData.rows}
                                rowCount={gridData.row_count}
                                getRowId={(row) => row.company_id}
                                filterMode="server"
                                paginationMode="server"
                                sortingMode="server"
                                pagination
                                page={gridState.page}
                                pageSize={gridState.page_size}
                                onFilterModelChange={onGridFilterChange}
                                onSortModelChange={onGridSortChange}
                                onPageChange={onGridPageChange}
                                onPageSizeChange={onGridPageSizeChange}
                                rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                headerHeight={DaGridSettings.headerRowHeight}
                                rowHeight={DaGridSettings.rowHeight}
                                isRowSelectable={({ row }) => !row.is_deleted}
                                checkboxSelection
                                disableSelectionOnClick
                                selectionModel={selectionModel}
                                onSelectionModelChange={(newSelection) => {
                                    setSelectionModel(newSelection);
                                }}
                                columnVisibilityModel={{
                                    customer_id: false,
                                    // federal_tax_id: false,
                                }}
                            />
                        </DaGridContainer>
                    </Box>
                ) : (
                    <Typography variant="h5">Please Login to use this system...</Typography>
                )}
            </Box>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgBoxOpen}
                onClose={dlgBox_Close}
                {...dlgBoxProps}
            />
        </>
    );
}
